/**
 * @ourbranch/env-helpers — ESM entry point
 *
 * Mirrors src/index.js (CommonJS) for ESM consumers like Vite/Rollup-built
 * frontends and TypeScript lambdas. Both files MUST stay in sync — keep
 * the logic identical when changing either one.
 *
 * Routing is handled by the `exports` field in package.json:
 *   - `import { isProductionEnv } from '@ourbranch/env-helpers'` → this file
 *   - `require('@ourbranch/env-helpers')` → src/index.js
 */

export const PROTECTED_ENVS = ['march2019', 'preprod'];

export const EPHEMERAL_ENVS = [
  'demo',
  'staging',
  'staging1',
  'staging2',
  'staging3',
  'staging4',
  'staging5',
  'staging6',
  'staging7',
  'staging8',
  'staging9'
];

/**
 * Returns true if the given environment name is a production environment.
 * Matches both 'production' (Branch) and '{carrier}-production' (e.g., forreston-production).
 */
export function isProductionEnv(env) {
  return env?.endsWith('production') ?? false;
}

/**
 * Returns true if the given environment name is a protected environment.
 * Protected environments include all production environments plus march2019 and preprod.
 */
export function isProtectedEnv(env) {
  return isProductionEnv(env) || PROTECTED_ENVS.includes(env);
}

/**
 * Returns true if the given environment name is an ephemeral environment.
 * Ephemeral environments are safe to write short-lived / disposable data to
 * (demo and the 10 staging CI slots).
 */
export function isEphemeralEnv(env) {
  return EPHEMERAL_ENVS.includes(env);
}

/**
 * Builds a fully-qualified Staff-app URL by joining a base host and a path.
 *
 * `staffDomain` is the full base URL (e.g. https://staff.ourbranch.com), sourced from the
 * STAFF_DOMAIN Doppler/SSM setting by the caller — it is NOT inferred here, because the
 * per-environment and per-carrier host can only come from configuration. Throws when
 * staffDomain is falsy so a missing STAFF_DOMAIN fails loudly rather than producing a
 * broken relative link. Normalizes the join (single slash) so callers don't have to
 * reason about trailing/leading slashes.
 */
export function staffLink(staffDomain, path) {
  if (!staffDomain) {
    throw new Error('staffLink requires a staffDomain — source it from the STAFF_DOMAIN SSM/Doppler setting');
  }
  const base = staffDomain.replace(/\/+$/, '');
  const normalizedPath = String(path).replace(/^\/+/, '');
  return `${base}/${normalizedPath}`;
}
